import type { Cents, HolisticCategory } from '@fruitful/types';

import type { OnboardingStatus } from '../../user/types/User';
import type { Card } from './Card';

export { HolisticCategory } from '@fruitful/types';

export const FRUITFUL_INSTITUTION_NAME = 'Fruitful';
export const DEFAULT_SAVINGS_ACCOUNT_NICKNAME = 'Savings';
export const DEFAULT_INDIVIDUAL_CHECKING_ACCOUNT_NICKNAME = 'My Cash';
export const DEFAULT_JOINT_CHECKING_ACCOUNT_NICKNAME = 'Our Cash';

export const SPACE_NAME_MAX_LENGTH = 20;

export enum AccountType {
    SAVING = 'SAVING',
    CHECKING = 'CHECKING',
    CHARGE_SECURED = 'CHARGE_SECURED',
}

export enum AccountOwnership {
    INDIVIDUAL = 'individual',
    JOINT = 'joint',
}

export enum RelationshipStatus {
    ACTIVE = 'ACTIVE',
    PENDING = 'PENDING',
}

export enum KYCVerificationStatus {
    UNVERIFIED = 'UNVERIFIED',
    PENDING = 'PENDING',
    PROVISIONAL = 'PROVISIONAL',
    ACCEPTED = 'ACCEPTED',
    REVIEW = 'REVIEW',
    VENDOR_ERROR = 'VENDOR_ERROR',
    REJECTED = 'REJECTED',
}

export interface BankAccount {
    id: string;
    syncteraAccountId: string | null;
    accountType: string | null;
    isActive: boolean;
}

export interface UserInfoSummary {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    legalFirstName?: string;
    legalLastName?: string;
    email?: string | null;
    onboardingStatus?: OnboardingStatus | null;
}

export interface AccountDetail {
    bankAccountId: string;
    accountType: AccountType;
    syncteraAccountId: string;
    chargeSecuredSyncteraAccountId?: string;
    currentBalanceCents: Cents;
    availableToWithdrawCents: Cents;
    cardBalanceCents?: Cents;
    interestEarnedCents: Cents;
    cashBackEarnedCents?: Cents;
    pendingTransfersCents?: Cents;
    pendingCardTransactionsCents?: Cents;
    accountNumber: string;
    accountNumberMasked: string;
    routingNumber: string;
    primaryUser: UserInfoSummary;
    jointUser?: UserInfoSummary | null;
    jointUserRelationshipStatus?: RelationshipStatus;
    nickname?: string | null;
    icon?: string | null;
    color?: string | null;
    creationTime?: string | null;
    holisticCategory?: HolisticCategory;
    autoPayEnabled?: boolean | null;
    autoPaymentDate?: string | null;
    paymentDueDate?: string | null;
    balanceDueCents?: Cents;
    daysPastDue?: number | null;
    cards?: Card[];
    accountStatus?: string | null;
    /** This account's current nominal rate as a fraction (4.88% = 0.0488). */
    interestRate?: number;
    /** This account's current APY as a fraction (5% = 0.05). */
    apy?: number;
}

export interface BankAccountDisplayDetail {
    bankAccountId: string;
    accountType: AccountType;
    hasCard: boolean;
    syncteraAccountId: string;
    chargeSecuredSyncteraAccountId?: string;
    accountNumber: string;
    accountNumberMasked: string;
    availableToWithdraw: string;
    currentBalance: string;
    interestEarned: string;
    cardBalance?: string;
    cashBackEarned?: string;
    pendingTransfers?: string;
    pendingCardTransactions?: string;
    hasPendingTransactions: boolean;
    routingNumber: string;
    routingNumberMasked: string;
    primaryUser: UserInfoSummary;
    jointUser?: UserInfoSummary | null;
    jointUserRelationshipStatus?: RelationshipStatus;
    nickname?: string | null;
    icon?: string | null;
    color?: string | null;
    holisticCategory: HolisticCategory;
    autoPayEnabled?: boolean | null;
    autoPaymentDate?: string | null;
    paymentDueDate?: string | null;
    balanceDue?: string;
    daysPastDue?: number | null;
    cards?: Card[];
    accountStatus?: string | null;
    creationTime?: string | null;
    /** This account's current nominal rate as a fraction (4.88% = 0.0488). */
    interestRate?: number;
    /** This account's current APY as a fraction (5% = 0.05). */
    apy?: number;
}

export interface SavingsDetails {
    totalAvailableBalanceCents: Cents;
    totalInterestEarnedCents: Cents;
    bankAccountDetails: AccountDetail[];
}

export interface AllAccountDetails {
    totalCurrentBalanceCents: Cents;
    totalAvailableBalanceCents: Cents;
    totalCardBalanceCents: Cents;
    totalInterestEarnedCents: Cents;
    totalCashBackEarnedCents: Cents;
    totalPendingTransfersCents: Cents;
    totalPendingCardTransactionsCents: Cents;
    bankAccountDetails: AccountDetail[];
}

export interface ExternalAccount {
    accessToken: string;
    customerId: string;
    id: string;
    institutionId: string;
    syncteraAccountId: string;
    vendor: string;
}

// The types of Disclosures that we must send to Synctera: https://dev.synctera.com/reference/listdisclosures
export enum DisclosureType {
    ACH_AUTHORIZATION = 'ACH_AUTHORIZATION',
    CARDHOLDER_AGREEMENT = 'CARDHOLDER_AGREEMENT',
    E_SIGN = 'E_SIGN',
    KYC_DATA_COLLECTION = 'KYC_DATA_COLLECTION',
    PRIVACY_NOTICE = 'PRIVACY_NOTICE',
    REG_CC = 'REG_CC',
    REG_DD = 'REG_DD',
    REG_E = 'REG_E',
    TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
    SMARTCARD_ACCOUNT_AGREEMENT = 'SC_ACCOUNT_AGREEMENT',
    SMARTCARD_SECURITY_AGREEMENT = 'SC_SECURITY_AGREEMENT',
    SMARTCARD_AUTOPAYMENT = 'SC_AUTO_PAYMENT',
}

// Sync'd with Synctera's disclosure type enum: https://dev.synctera.com/reference/createdisclosure
export enum DisclosureEvent {
    DISPLAYED = 'DISPLAYED',
    VIEWED = 'VIEWED',
    ACKNOWLEDGED = 'ACKNOWLEDGED',
}

export interface Disclosure {
    type: DisclosureType;
    eventType: DisclosureEvent;
    version: string;
    metadata: undefined | { url: string; description: string };
}

export interface UpdateBankAccountInfo {
    nickname?: string;
    icon?: string;
    color?: string;
    jointEmail?: string;
    holisticCategory?: HolisticCategory;
}

export enum SyncteraAccountStatus {
    CLOSED = 'CLOSED',
    ACTIVE_OR_DISBURSED = 'ACTIVE_OR_DISBURSED',
}
